import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RawTrawledPaper } from "@/services/dedup";
import { resolveS2Id, postBatches, deduplicatePapers, fetchPaperNetwork } from "@/lib/trawl-utils";
import { S2AuthError } from "@/services/s2-client";
import { useTrawlStore } from "@/stores/trawl-store";
import { toast } from "@/lib/toast";
import { postRunTiming, toastJobFinished, type RunOutcome } from "@/lib/runTiming";
import { cachedPapers, openPageRun } from "@/lib/pageRun";
import { remainingSeeds } from "@/lib/jobRuns";
import { useActivityStore } from "@/stores/activity-store";
import type { JobRun } from "@/hooks/useActivity";
import type { PaperWithRole } from "@/types/paper";
import { RECENCY_WEIGHTS, type DirectionWeights } from "@/lib/setup/recency";
import { effectiveScope } from "@/lib/angles";
import type { Concept, ConceptScope, Recency } from "@/types/concept";

/** Spec §3: recency tilts trawl directions; no recency = today's limits. */
export function trawlWeightsFor(scope: ConceptScope | null | undefined): DirectionWeights | undefined {
  return scope?.recency ? RECENCY_WEIGHTS[scope.recency] : undefined;
}

/** Facets 1c-3 (D10): a seed trawls with its first active angle's effective recency;
 * an untagged seed (or one tagged only with retired angles) uses the concept's. */
export function seedRecency(seed: Pick<PaperWithRole, "seed_facet_ids">, concept: Concept | undefined): Recency | undefined {
  const facets = concept?.facet_list ?? [];
  for (const id of seed.seed_facet_ids ?? []) {
    const f = facets.find((x) => x.id === id);
    if (f) return effectiveScope(f, concept?.scope).recency ?? concept?.scope?.recency;
  }
  return concept?.scope?.recency;
}

export function useTrawl(conceptId: string) {
  const queryClient = useQueryClient();

  const trawl = useCallback(
    async (foundationalPapers: PaperWithRole[], opts: { resume?: JobRun } = {}) => {
      if (foundationalPapers.length === 0) return;

      const {
        tryStartTrawl,
        updateProgress,
        completeTrawl,
        failTrawl,
        options,
      } = useTrawlStore.getState();

      // Read the cache at call time (not render time) so a stale closure can't use an old scope.
      const concept = queryClient.getQueryData<Concept>(["concepts", conceptId]);

      // Re-entry guard: two trawls would race one progress store and double-post batches.
      const controller = tryStartTrawl();
      if (!controller) {
        toast.info("A trawl is already running");
        return;
      }
      const signal = controller.signal;
      const startedAt = Date.now();
      let outcome: RunOutcome = "cancelled";
      let endText: string | undefined;
      // Durable jobs: each seed's papers are saved as it finishes and the run
      // records which seeds are done, so a closed page can Resume.
      const run = await openPageRun(
        queryClient, conceptId, "trawl", foundationalPapers.map((p) => p.id), opts.resume,
      );
      const saved = { added: 0, duplicates: 0, merged: 0 };
      let saveChain: Promise<void> = Promise.resolve();
      let saveError: unknown = null;
      const saveSeed = (seedId: string, papers: RawTrawledPaper[]) => {
        if (!run) return;
        saveChain = saveChain.then(async () => {
          if (saveError || signal.aborted) return;
          try {
            const unique = deduplicatePapers(papers);
            if (unique.length > 0) {
              const r = await postBatches(conceptId, unique, signal);
              saved.added += r.added;
              saved.duplicates += r.duplicates;
              saved.merged += r.merged;
            }
            run.seedDone(seedId);
          } catch (err) {
            saveError = err;
          }
        });
      };

      try {
        updateProgress({
          totalPapers: foundationalPapers.length,
          statusMessage: "Starting trawl...",
        });

        // --- Phase 1: Resolve all S2 IDs concurrently ---
        updateProgress({
          currentOperation: "Resolving S2 IDs",
          statusMessage: `Resolving ${foundationalPapers.length} seed papers...`,
        });

        const resolved = await Promise.all(
          foundationalPapers.map(async (paper) => {
            if (signal.aborted) return { paper, s2Id: null as string | null };
            const s2Id = await resolveS2Id(paper, signal);
            return { paper, s2Id };
          }),
        );

        if (signal.aborted) return;

        const validPapers = resolved.filter((r) => r.s2Id != null) as {
          paper: PaperWithRole;
          s2Id: string;
        }[];
        const skipped = resolved
          .filter((r) => r.s2Id == null)
          .map((r) => r.paper.title);
        // An unresolvable seed won't resolve on Resume either.
        for (const r of resolved) if (r.s2Id == null) run?.seedDone(r.paper.id);

        // --- Phase 2: Fetch networks for all papers concurrently ---
        // All calls share the rate limiter, so concurrency overlaps network
        // latency without exceeding the S2 rate limit.
        updateProgress({
          currentOperation: "Fetching citation networks",
          statusMessage: `Fetching networks for ${validPapers.length} papers...`,
          percent: 20,
        });

        let completedNetworks = 0;
        let totalCitationsSoFar = 0;
        const pendingTitles = new Set(validPapers.map((v) => v.paper.title));

        const networkResults = await Promise.all(
          validPapers.map(async ({ paper, s2Id }) => {
            if (signal.aborted) return [];
            const recency = seedRecency(paper, concept);
            try {
              const papers = await fetchPaperNetwork(s2Id, {
                maxResults: options.maxResults,
                includeReferences: options.includeReferences,
                includeRecommendations: options.includeRecommendations,
                signal,
                directionWeights: trawlWeightsFor({ recency }),
                recency,
                parent: { paperId: paper.id, method: "trawl" },
                onProgress: (resultsSoFar) => {
                  // Sub-progress: update as individual S2 pages arrive
                  totalCitationsSoFar++;
                  const basePercent = 20 + Math.round((completedNetworks / validPapers.length) * 60);
                  const subPercent = Math.round((1 / validPapers.length) * 60 * Math.min(resultsSoFar / Math.max(options.maxResults, 1), 1));
                  updateProgress({
                    citationsFound: totalCitationsSoFar,
                    percent: Math.min(79, basePercent + subPercent),
                    statusMessage: completedNetworks < validPapers.length - 1
                      ? `Fetched ${completedNetworks}/${validPapers.length} networks (${totalCitationsSoFar} papers found)...`
                      : `Finishing last network: ${paper.title.slice(0, 50)}... (${totalCitationsSoFar} papers found)`,
                  });
                },
              });
              completedNetworks++;
              pendingTitles.delete(paper.title);
              totalCitationsSoFar = papers.length + (totalCitationsSoFar - papers.length); // reconcile

              // Show remaining paper titles when near the end
              const remaining = [...pendingTitles];
              const statusMsg = remaining.length === 0
                ? `Fetched all ${validPapers.length} networks (${totalCitationsSoFar} papers found)`
                : remaining.length === 1
                  ? `${completedNetworks}/${validPapers.length} done — finishing: ${remaining[0].slice(0, 60)}`
                  : `${completedNetworks}/${validPapers.length} done — ${remaining.length} remaining...`;

              updateProgress({
                papersProcessed: completedNetworks,
                currentPaperTitle: paper.title,
                percent: 20 + Math.round((completedNetworks / validPapers.length) * 60),
                statusMessage: statusMsg,
              });
              saveSeed(paper.id, papers);
              return papers;
            } catch (err) {
              if (signal.aborted) return [];
              // Signed out: fail the trawl rather than finish with "No new papers".
              if (err instanceof S2AuthError) throw err;
              completedNetworks++;
              pendingTitles.delete(paper.title);
              return [];
            }
          }),
        );

        if (signal.aborted) return;
        await saveChain;
        if (saveError) throw saveError;

        const allPapers: RawTrawledPaper[] = networkResults.flat();
        const totalCitations = allPapers.length;

        updateProgress({
          citationsFound: totalCitations,
          percent: 80,
        });

        // --- Phase 3: Dedup + POST ---
        updateProgress({
          currentOperation: "Deduplicating",
          statusMessage: "Removing duplicates...",
        });
        const unique = deduplicatePapers(allPapers);

        if (unique.length > 0) {
          updateProgress({
            currentOperation: "Saving to server",
            statusMessage: `Saving ${unique.length} papers...`,
            percent: 85,
          });
          // With a run, each seed's papers were saved as it finished.
          const result = run ? saved : await postBatches(conceptId, unique, signal);

          await queryClient.invalidateQueries({
            queryKey: ["papers", conceptId],
          });
          await queryClient.invalidateQueries({ queryKey: ["concepts"] });

          const parts = [
            `${result.added} new papers added`,
            result.duplicates > 0 ? `${result.duplicates} duplicates skipped` : "",
            result.merged > 0 ? `${result.merged} merged` : "",
            skipped.length > 0 ? `${skipped.length} papers couldn't be resolved` : "",
          ].filter(Boolean);

          completeTrawl(parts.join(", "));
          outcome = "success";
          endText = `${result.added} new papers`;
          toastJobFinished(conceptId, "Trawl finished", parts.join(", "));
        } else {
          const message = skipped.length > 0
            ? `No new papers found. ${skipped.length} papers couldn't be resolved.`
            : "No new papers found.";
          completeTrawl(message);
          outcome = "success";
          endText = "No new papers";
          toastJobFinished(conceptId, "Trawl finished", message);
        }
      } catch (err) {
        if (!signal.aborted) {
          outcome = "failed";
          endText = err instanceof Error ? err.message : "Trawl failed";
          failTrawl(endText);
          // Other seeds are still fetching: stop them saving after the failure.
          if (run) controller.abort();
        }
      } finally {
        postRunTiming(conceptId, "trawl", foundationalPapers.length, startedAt, outcome);
        run?.end(outcome === "success" ? "done" : outcome, endText);
        // Saved per seed: show them even when the run stopped early.
        if (run && (saved.added > 0 || saved.merged > 0) && outcome !== "success") {
          void queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
          void queryClient.invalidateQueries({ queryKey: ["paper-keys", conceptId] });
          void queryClient.invalidateQueries({ queryKey: ["concepts"] });
        }
      }
    },
    [conceptId, queryClient],
  );

  // Resume a trawl the page stopped (Activity panel): only the seeds it
  // hadn't finished, on the same run.
  useEffect(() => {
    const resume = (run: JobRun) => {
      const left = new Set(remainingSeeds(run));
      const seeds = cachedPapers(queryClient, conceptId).filter((p) => left.has(p.id));
      if (seeds.length === 0) {
        toast.info("Those seeds are no longer in this concept");
        return;
      }
      void trawl(seeds, { resume: run });
    };
    const { registerResumer } = useActivityStore.getState();
    registerResumer("trawl", resume);
    return () => registerResumer("trawl", null);
  }, [trawl, queryClient, conceptId]);

  return { trawl };
}
